import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, MapPin, Star, HardHat, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FavoritesPage = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const res = await axios.get(`${API}/favorites`, { withCredentials: true });
        setFavorites(res.data || []);
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  const removeFavorite = async (contractorId) => {
    try {
      await axios.delete(`${API}/favorites/${contractorId}`, { withCredentials: true });
      setFavorites(prev => prev.filter(f => f.id !== contractorId));
      toast.success('Retiré des favoris');
    } catch (err) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="favorites-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="favorites-title">
            Mes Favoris
          </h1>
          <p className="text-[#4B5563] mt-1">Vos sous-traitants préférés</p>
        </div>

        {favorites.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {favorites.map((contractor) => (
              <Card key={contractor.id} className="card-surface hover:border-[#1E3A8A]/30 transition-colors" data-testid={`fav-card-${contractor.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <Link to={`/contractors/${contractor.id}`} className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                          <HardHat className="w-6 h-6 text-[#1E3A8A]" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-[#111827]">{contractor.company_name}</h3>
                          <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                            <MapPin className="w-3 h-3" />
                            {contractor.city}, {contractor.country}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                          <span className="text-sm font-medium">{contractor.rating?.toFixed(1) || '0.0'}</span>
                        </div>
                        {contractor.is_verified && (
                          <Badge className="badge-verified text-xs flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Vérifié
                          </Badge>
                        )}
                        {contractor.is_available && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Disponible</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {contractor.trades?.slice(0, 3).map((trade, i) => (
                          <Badge key={trade} className="badge-trade text-xs">{trade}</Badge>
                        ))}
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                      onClick={() => removeFavorite(contractor.id)}
                      data-testid={`remove-fav-${contractor.id}`}
                    >
                      <Heart className="w-5 h-5 fill-current" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Heart className="w-12 h-12 mx-auto text-[#6B7280]/30 mb-4" />
              <p className="text-[#4B5563] mb-2">Aucun favori</p>
              <p className="text-sm text-[#6B7280] mb-4">Parcourez les sous-traitants et ajoutez-les à vos favoris</p>
              <Link to="/contractors">
                <Button className="btn-primary">Trouver des sous-traitants</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
